import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import { InjectionToken, NgModule, isDevMode } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  FIREBASE_APP,
  PageTitleService,
  REPORTING_API_CONFIG,
  provideAgentSearch,
  ReportingApiAuthInterceptor,
  ReportingApiBaseUrlInterceptor,
  USE_TEMP_MEDIA_UPLOAD,
} from 'ag-common-svc/public-api';
import './date.prototype';
import { CommonModule } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ErrorInterceptor } from './auth/helpers/error.interceptor';
import { ReportingApiImpersonationInterceptor } from './auth/helpers/reporting-api-impersonation.interceptor';
import { IonicModule } from '@ionic/angular';
import {
  STRIPE_PUBLIC_KEY_LIVE,
  STRIPE_PUBLIC_KEY_TEST,
} from 'ag-common-svc/lib/components/ag-stripe-modal/ag-stripe-modal.models';
import { provideNgxStripe } from 'ngx-stripe';
import {
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  initializeFirestore,
  FirestoreLocalCache,
} from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from 'ag-common-lib/lib/models/utils/google-maps-injection-token';
import { USE_CLOUD_FUNCTIONS_EMULATOR } from 'ag-common-svc/lib/injections/use-cloud-functions-emulator';

export const fireBaseApp: FirebaseApp = initializeApp(environment.firebaseConfig);

const db = initializeFirestore(fireBaseApp, {
  experimentalForceLongPolling: true,
  localCache: { kind: 'persistent' },
  ignoreUndefinedProperties: true,
  // cacheSizeBytes: CACHE_SIZE_UNLIMITED,
});

if (environment.emulators?.firestore?.enabled) {
  connectFirestoreEmulator(db, environment.emulators.firestore.host, environment.emulators.firestore.port);
}

if (environment.emulators?.storage?.enabled) {
  const storage = getStorage(fireBaseApp);
  connectStorageEmulator(storage, environment.emulators.storage.host, environment.emulators.storage.port);
}

try {
  enableIndexedDbPersistence(db).catch(error => {});
} catch (error) {}

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    IonicModule.forRoot(),
    TranslateModule.forRoot({
      fallbackLang: 'en',
      lang: 'en',
      loader: provideTranslateLoader(TranslateHttpLoader),
    }),
    AppRoutingModule,
    ServiceWorkerModule.register('main-sw.js', {
      enabled: environment.isPWAEnabled,
      scope: '/',
      registrationStrategy: 'registerImmediately',
    }),
  ],

  providers: [
    PageTitleService,
    { provide: TitleStrategy, useExisting: PageTitleService },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: 'assets/i18n/', suffix: '.json' },
    },
    provideNgxStripe(),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ReportingApiBaseUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ReportingApiAuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ReportingApiImpersonationInterceptor, multi: true },
    {
      provide: FIREBASE_APP,
      useValue: fireBaseApp,
    },
    {
      provide: USE_CLOUD_FUNCTIONS_EMULATOR,
      useValue: environment.emulators?.functions?.enabled ?? false,
    },
    {
      provide: USE_TEMP_MEDIA_UPLOAD,
      useValue: environment.useTempMediaUpload,
    },
    {
      provide: STRIPE_PUBLIC_KEY_LIVE,
      useValue: environment.stripe_payments_public_key_live,
    },
    {
      provide: STRIPE_PUBLIC_KEY_TEST,
      useValue: environment.stripe_payments_public_key_test,
    },
    {
      provide: GOOGLE_MAPS_API_KEY,
      useValue: environment.google.mapsAPIKey,
    },
    {
      provide: GOOGLE_MAPS_MAP_ID,
      useValue: environment.google.mapId,
    },
    {
      provide: REPORTING_API_CONFIG,
      useValue: environment.reportingApi,
    },
    // Binds AgentSearchService to the SQL-backed implementation.
    ...provideAgentSearch(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
